import React from 'react';

export interface PartyData {
  id?: number;
  name?: string;
  abbreviation?: string | null;
  party_colors?: string[] | null;
}

interface PartyChipProps {
  party?: PartyData | null;
  /** Set on dark backgrounds, e.g. the president hero. */
  onDark?: boolean;
  className?: string;
}

/**
 * Party affiliation shown as data.
 *
 * The site's own palette is deliberately achromatic, because Uganda's parties
 * are each identified with a colour and a directory covering all of them
 * cannot adopt any of those hues. This chip is the one place a party's colour
 * belongs: it is a fact about the person, rendered identically for every
 * leader, so it states affiliation without the page taking a side.
 *
 * PoliticalParty.party_colors is empty for most parties today, so the chip
 * leads with the name and abbreviation and only draws swatches when colours
 * have actually been recorded. It renders nothing at all without a party,
 * rather than showing an empty shell.
 */
const PartyChip: React.FC<PartyChipProps> = ({ party, onDark = false, className = '' }) => {
  if (!party || !party.name) return null;

  const colors = (party.party_colors || []).filter(Boolean);
  const label = party.abbreviation || party.name;

  const shell = onDark
    ? 'border-white/35 bg-white/10 text-white'
    : 'border-outline-subtle bg-surface-lowest text-on-surface';
  const detail = onDark ? 'text-white/75' : 'text-on-surface-variant';

  return (
    <span data-component-id="src/components/common/PartyChip.tsx:43:4" data-component-path="src/components/common/PartyChip.tsx" data-component-line="43" data-component-end-line="61" data-component-file="PartyChip.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
      className={`inline-flex items-center gap-2 rounded border px-4 py-2 ${shell} ${className}`}
      title={party.name}
    >
      {colors.length > 0 && (
        <span data-component-id="src/components/common/PartyChip.tsx:48:8" data-component-path="src/components/common/PartyChip.tsx" data-component-line="48" data-component-end-line="55" data-component-file="PartyChip.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20h-3%20w-6%20shrink-0%20overflow-hidden%20rounded-sm%22%7D"
          className="flex h-3 w-6 shrink-0 overflow-hidden rounded-sm"
          aria-hidden="true"
        >
          {colors.map((color, i) => (
            <span data-component-id="src/components/common/PartyChip.tsx:53:12" data-component-path="src/components/common/PartyChip.tsx" data-component-line="53" data-component-end-line="53" data-component-file="PartyChip.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22h-full%20flex-1%22%7D" key={i} className="h-full flex-1" style={{ backgroundColor: color }} />
          ))}
        </span>
      )}
      <span data-component-id="src/components/common/PartyChip.tsx:57:6" data-component-path="src/components/common/PartyChip.tsx" data-component-line="57" data-component-end-line="57" data-component-file="PartyChip.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-label-caps%20uppercase%22%7D" className="text-label-caps uppercase">{label}</span>
      {party.abbreviation && party.name !== party.abbreviation && (
        <span data-component-id="src/components/common/PartyChip.tsx:59:8" data-component-path="src/components/common/PartyChip.tsx" data-component-line="59" data-component-end-line="59" data-component-file="PartyChip.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`text-label-sm normal-case ${detail}`}>{party.name}</span>
      )}
    </span>
  );
};

export default PartyChip;
